import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserApi, UsersFilter } from '../models';
import { Authorize, DeleteConfirmModal, Table } from '@/components';
import { BUSINESS_ROLE_GROUPS } from '@/components/security/AppRoles';
import {
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { usersTableColumns } from '../config/columns';
import { useUsers, useUserActions } from '../hooks';
import { IconButton } from '@mui/material';

type UsersTableProps = {
  usersFilter?: UsersFilter;
  currentPage: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
};

const UsersTable = ({
  usersFilter,
  currentPage,
  pageSize,
  onPageChange,
  onPageSizeChange,
}: UsersTableProps) => {
  const navigate = useNavigate();
  const columns = useMemo(() => usersTableColumns, []);

  const { users, isUsersLoading, totalItems, totalPages } = useUsers({
    paginated: {
      page: currentPage,
      pageSize: pageSize,
    },
    filters: usersFilter,
  });

  const { onDeleteUser, isDeletingUser } = useUserActions();
  const [userToDelete, setUserToDelete] = useState<UserApi | null>(null);

  const userToDeleteName = userToDelete
    ? `${userToDelete.firstName} ${userToDelete.lastName}`.trim()
    : '';

  const handleDeleteConfirm = async () => {
    if (!userToDelete) return;
    await onDeleteUser(userToDelete.id);
    setUserToDelete(null);
  };

  const renderActions = (rowData: Record<string, unknown>) => {
    const user = rowData as unknown as UserApi;

    return (
      <>
        <IconButton onClick={() => navigate(`/business/users/${user.id}`)}>
          <VisibilityIcon />
        </IconButton>
        <Authorize permission={BUSINESS_ROLE_GROUPS.management} fallback={null}>
          <IconButton
            onClick={() => navigate(`/business/users/${user.id}/edit`)}
          >
            <EditIcon />
          </IconButton>
        </Authorize>
        <Authorize permission={BUSINESS_ROLE_GROUPS.management} fallback={null}>
          <IconButton onClick={() => setUserToDelete(user)}>
            <DeleteIcon />
          </IconButton>
        </Authorize>
      </>
    );
  };

  return (
    <div className='w-full p-5 flex flex-col bg-white rounded-lg shadow-md gap-5'>
      <Table
        columns={columns}
        data={users}
        renderActions={renderActions}
        isLoading={isUsersLoading}
        pagination={{
          totalItems,
          totalPages,
          currentPage,
          rowsPerPage: pageSize,
          onPageChange,
          onRowsPerPageChange: onPageSizeChange,
        }}
      />
      <DeleteConfirmModal
        open={!!userToDelete}
        title='¿Eliminar usuario?'
        description={
          <>
            Vas a eliminar a <strong>{userToDeleteName}</strong> (
            {userToDelete?.email}). No va a poder ingresar más y la eliminación
            no se puede deshacer desde el panel.
          </>
        }
        isLoading={isDeletingUser}
        onClose={() => setUserToDelete(null)}
        onConfirm={handleDeleteConfirm}
      />
    </div>
  );
};

export default UsersTable;
